import { useEffect, useRef, useState } from 'react'
import { supabase, isSupabaseConfigured } from '../lib/supabase'
import { useAuth } from '../context/AuthContext'
import SongCard from '../components/SongCard'
import SEO from '../components/SEO'
import './CatalogPage.css'

const SKELETON_CARD_COUNT = 8

const GENRES = ['all', 'children', 'pop', 'classical', 'folk', 'anime', 'other']
const DIFFICULTIES = ['all', 'beginner', 'intermediate', 'advanced']
const SORT_OPTIONS = [
  { value: 'popular', label: 'Most Popular' },
  { value: 'newest', label: 'Newest' },
  { value: 'az', label: 'A → Z' },
]

export default function CatalogPage() {
  const { user } = useAuth()
  const [songs, setSongs] = useState([])
  const [favorites, setFavorites] = useState([])
  const [initialLoading, setInitialLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [loadError, setLoadError] = useState(null)
  const [search, setSearch] = useState('')
  const [genre, setGenre] = useState('all')
  const [difficulty, setDifficulty] = useState('all')
  const [sort, setSort] = useState('newest')
  const loadedOnceRef = useRef(false)

  useEffect(() => {
    let cancelled = false
    async function fetchSongs() {
      const isFirstLoad = !loadedOnceRef.current
      if (isFirstLoad) setInitialLoading(true)
      else setRefreshing(true)
      setLoadError(null)
      try {
        if (!isSupabaseConfigured) {
          if (!cancelled) {
            setSongs([])
            setLoadError(
              'Supabase env vars are missing. Copy .env.example to .env, set REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY, then restart npm start.',
            )
          }
          return
        }
        const versionRel = difficulty === 'all'
          ? 'song_versions(difficulty)'
          : 'song_versions!inner(difficulty)'
        let query = supabase
          .from('songs')
          .select(`*, ${versionRel}`)
          .eq('is_published', true)
        if (genre !== 'all') query = query.eq('genre', genre)
        if (difficulty !== 'all') query = query.eq('song_versions.difficulty', difficulty)
        if (sort === 'popular') query = query.order('play_count', { ascending: false })
        else if (sort === 'newest') query = query.order('updated_at', { ascending: false })
        else if (sort === 'az') query = query.order('title', { ascending: true })
        const { data, error } = await query
        if (cancelled) return
        if (error) throw error
        const enriched = (data ?? []).map(s => {
          const diffs = Array.from(new Set((s.song_versions ?? []).map(v => v.difficulty).filter(Boolean)))
          const order = { beginner: 0, intermediate: 1, advanced: 2 }
          diffs.sort((a, b) => (order[a] ?? 99) - (order[b] ?? 99))
          return { ...s, difficulties: diffs }
        })
        setSongs(enriched)
        setLoadError(null)
      } catch (e) {
        if (!cancelled) {
          setSongs([])
          setLoadError(e?.message ?? 'Could not load songs. Check the network and Supabase project.')
        }
      } finally {
        if (!cancelled) {
          loadedOnceRef.current = true
          setInitialLoading(false)
          setRefreshing(false)
        }
      }
    }
    fetchSongs()
    return () => {
      cancelled = true
    }
  }, [genre, difficulty, sort])
  useEffect(() => { if (user) fetchFavorites() }, [user])

  async function fetchFavorites() {
    const { data } = await supabase
      .from('favorites')
      .select('song_id')
      .eq('user_id', user.id)
    setFavorites(data?.map(f => f.song_id) ?? [])
  }

  async function toggleFavorite(songId) {
    if (!user) return
    if (favorites.includes(songId)) {
      await supabase.from('favorites').delete()
        .eq('user_id', user.id).eq('song_id', songId)
      setFavorites(prev => prev.filter(id => id !== songId))
    } else {
      await supabase.from('favorites').insert({ user_id: user.id, song_id: songId })
      setFavorites(prev => [...prev, songId])
    }
  }

  const filtered = songs.filter(s =>
    s.title.toLowerCase().includes(search.toLowerCase())
  )

  const catalogSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: 'Kalimba Tabs Catalog',
    description: 'Browse free kalimba tabs for all levels.',
    url: 'https://kalimbaba.com/',
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: songs.slice(0, 20).map((song, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        url: `https://kalimbaba.com/song/${song.slug}`,
        name: song.title,
      })),
    },
  }

  return (
    <div className="catalog-page">
      <SEO
        title="Free Kalimba Tabs — Learn Thumb Piano"
        description="Browse free kalimba tabs for all levels. Search by song, genre, or difficulty and start playing your favourite songs today."
        canonicalPath="/"
        schema={catalogSchema}
      />

      {/* Hero */}
      <div className="catalog-hero">
        <h1 className="hero-title font-title">Kalimba Tabs</h1>
        <p className="hero-script font-script">Master the thumb piano in minutes.</p>
        <div className="hero-search-wrap">
          <input
            className="hero-search"
            type="search"
            placeholder="Search songs..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        <p className="hero-count" aria-live="polite">
          {initialLoading || loadError ? '\u00A0' : `${songs.length} songs available`}
        </p>
      </div>

      <div className="container">

        {/* Filters */}
        <div className={`filters${refreshing ? ' filters--refreshing' : ''}`}>
          <div className="filter-row">
            <FilterGroup label="Genre" value={genre} onChange={setGenre} options={GENRES} />
            <FilterGroup label="Level" value={difficulty} onChange={setDifficulty} options={DIFFICULTIES} />
            <select className="sort-select" value={sort} onChange={e => setSort(e.target.value)}>
              {SORT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
        </div>

        {/* Results */}
        <div className="catalog-results">
        {initialLoading ? (
          <div className="catalog-grid catalog-grid--skeleton" aria-busy="true" aria-label="Loading songs">
            {Array.from({ length: SKELETON_CARD_COUNT }, (_, i) => (
              <div key={i} className="song-card-skeleton" aria-hidden="true" />
            ))}
          </div>
        ) : loadError ? (
          <div className="empty-state catalog-load-error" role="alert">
            <p className="catalog-load-error-title">Could not load songs</p>
            <p className="catalog-load-error-detail">{loadError}</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="empty-state">No songs found. Try different filters.</div>
        ) : (
          <div className="catalog-grid">
            {filtered.map(song => (
              <SongCard
                key={song.id}
                song={song}
                isFavorited={favorites.includes(song.id)}
                onToggleFavorite={user ? toggleFavorite : null}
              />
            ))}
          </div>
        )}
        </div>

      </div>
    </div>
  )
}

function FilterGroup({ label, value, onChange, options }) {
  return (
    <div className="filter-group">
      <span className="filter-label">{label}</span>
      <div className="filter-pills">
        {options.map(opt => (
          <button
            key={opt}
            className={`pill ${value === opt ? 'active' : ''}`}
            onClick={() => onChange(opt)}
          >
            {opt === 'all' ? 'All' : opt.charAt(0).toUpperCase() + opt.slice(1)}
          </button>
        ))}
      </div>
    </div>
  )
}
